import React from 'react';
import { useNavigation } from '../../../utils/redirectUtils';
import { trackPostHogEvent } from '../../../utils/posthogUtils';
import Pricing from '../../../data/Pricing';
import styles from './WhatsIncluded.module.css';
import kitImage from '../../../assets/kit-flatlay.webp';

// Numbered callouts over the kit photo. left/top are percentages within the frame.
const KIT_PINS = [
    { num: '1', label: 'Medication vial', pos: 'pinA' },
    { num: '2', label: 'Alcohol prep pads', pos: 'pinB' },
    { num: '3', label: 'Syringes & needles', pos: 'pinC' },
];

const ITEMS = [
    {
        title: 'Telehealth visits with your clinician',
        body: "Talk through your prescription, dosing, and anything you're unsure about.",
        meta: 'Included',
        icon: (
            <svg viewBox="0 0 24 24"><path d="M5 3v5.5a4 4 0 0 0 8 0V3"></path><path d="M9 12.5v3.5a4.5 4.5 0 0 0 9 0v-1.2"></path><circle cx="18.5" cy="12" r="2.3"></circle><path d="M3.6 3h2.8M11.6 3h2.8"></path></svg>
        ),
    },
    {
        title: 'Patient care coordinators',
        body: 'Real people who answer questions about your order, shipping, and program.',
        meta: 'Available every day',
        icon: (
            <svg viewBox="0 0 24 24"><path d="M4 13a8 8 0 0 1 16 0"></path><rect x="2.5" y="13" width="4" height="6.5" rx="2"></rect><rect x="17.5" y="13" width="4" height="6.5" rx="2"></rect><path d="M19.5 19.5a3 3 0 0 1-3 3H13"></path></svg>
        ),
    },
    {
        title: 'The Progress Tracker',
        body: 'Track weight, progress through the program, and log all changes as they happen.',
        meta: 'Always on',
        icon: (
            <svg viewBox="0 0 24 24"><rect x="6" y="2.5" width="12" height="19" rx="2.5"></rect><path d="M10.5 5.2h3"></path><path d="M9.2 14.6l2.3-2.7 1.9 1.9 2.6-3.4"></path><path d="M11 18.6h2"></path></svg>
        ),
    },
    {
        title: 'Your medication & supplies',
        body: 'Weekly or daily as prescribed, shipped to your door with everything required to take it.',
        meta: 'Free shipping',
        icon: (
            <svg viewBox="0 0 24 24"><path d="M1.8 6.2h11.4v10.3H1.8z"></path><path d="M13.2 9.6h4.2l2.9 3.1v3.8h-7.1z"></path><circle cx="6.2" cy="18.6" r="2.1"></circle><circle cx="16.6" cy="18.6" r="2.1"></circle><path d="M8.3 18.6h6.2M1.8 16.5h2.3"></path></svg>
        ),
    },
];

// Illustrative titration curve. `left`/`top` are percentages within the plot box.
const DOSE_POINTS = [
    { label: '0.25 mg', left: 10, top: 92 },
    { label: '0.5 mg', left: 30, top: 82 },
    { label: '1.0 mg', left: 50, top: 64 },
    { label: '1.7 mg', left: 70, top: 42 },
    { label: '2.4 mg', left: 90, top: 28, last: true },
];

const AXIS = [
    { month: 'Month 1', phase: 'starting dose' },
    { month: 'Month 2', phase: 'titration' },
    { month: 'Month 3', phase: 'titration' },
    { month: 'Month 4', phase: 'titration' },
    { month: 'Month 5+', phase: 'maintenance' },
];

const FINE_PRINT = [
    {
        title: 'Dosing & maintenance',
        body: 'Dosing is determined by your clinician — the doses shown above are illustrative, not a schedule. Your maintenance dose is set by your clinician based on how you respond, and may be held, raised, or lowered over time.',
    },
    {
        title: 'Not included',
        body: 'Lab work ordered outside the program · Treatment for conditions unrelated to your program · Expedited shipping upgrades.',
    },
    {
        title: 'Every journey is different',
        body: "Every patient's journey is different. Response to treatment varies from person to person and there are no guaranteed outcomes. Compounded medications are not FDA-approved and are prescribed only when clinically appropriate.",
    },
];

export default function WhatsIncluded() {
    const navigate = useNavigation();

    const pricing = Pricing();
    const semaglutide = pricing.getPricingOptionsByProductName('semaglutide');
    // Must match the hero's "starting at" figure: the 3-month plan's monthly
    // equivalent, which is the lowest per-month price for the same source.
    const monthlyPrice = semaglutide?.injectable?.threeMonths?.monthly_amount?.split('.')[0] ?? null;

    const handleSeePricing = () => {
        trackPostHogEvent('landing_page_submitted', {
            selected: 'whats_included_see_pricing',
            to_path: '/programs'
        });
        navigate('/programs');
    };

    return (
        <section className={styles.incl}>
            <div className={styles.inclInner}>

                <div className={styles.head}>
                    <div className={styles.eyebrow}>What's included</div>
                    <h2 className={styles.title}>
                        One monthly fee. <span className={styles.grad}>Everything you need is in it.</span>
                    </h2>
                    {monthlyPrice && (
                        <div className={styles.price}>
                            <div className={styles.priceAmt}>${monthlyPrice}<em>/month</em></div>
                            <div className={styles.priceNote}>No add-ons · No dose surcharge</div>
                        </div>
                    )}
                </div>

                <div className={styles.top}>
                    <div className={styles.kit}>
                        <img src={kitImage} alt="The monthly kit as shipped: a tb2.health compounded GLP-1 vial, alcohol prep pads, and syringes" />
                        <div className={styles.kitPins} aria-hidden="true">
                            {KIT_PINS.map((pin) => (
                                <div className={`${styles.pin} ${styles[pin.pos]}`} key={pin.num}>
                                    <span className={styles.pinNum}>{pin.num}</span>
                                    <span className={styles.pinLbl}>{pin.label}</span>
                                </div>
                            ))}
                        </div>
                        <div className={styles.kitCap}>
                            Everything for the month, at your front door.
                            <span>Supplies included — nothing to source yourself, nothing to buy at a pharmacy counter.</span>
                        </div>
                    </div>

                    <div className={styles.list}>
                        {ITEMS.map((item) => (
                            <div className={styles.item} key={item.title}>
                                <div className={styles.ico}>{item.icon}</div>
                                <div>
                                    <h4>{item.title}</h4>
                                    <p>{item.body}</p>
                                </div>
                                <div className={styles.meta}>{item.meta}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className={styles.ladder}>
                    <div className={styles.ladderLeft}>
                        <h3>Your dose goes up. Your bill doesn't.</h3>
                        <p>Clinician-guided dose increases are part of the program — never billed as an upgrade.</p>
                        <button type="button" className={styles.ladderCta} onClick={handleSeePricing}>
                            See full pricing
                        </button>
                    </div>

                    <div className={styles.chart}>
                        <div className={styles.plot}>
                            <svg
                                viewBox="0 0 1000 100"
                                preserveAspectRatio="none"
                                role="img"
                                aria-label={`Dose rises from 0.25 mg to 2.4 mg across five months while the monthly price stays flat${monthlyPrice ? ` at $${monthlyPrice}` : ''}`}
                            >
                                <defs>
                                    <linearGradient id="tb2DoseGradient" x1="0" y1="0" x2="1" y2="0">
                                        <stop offset="0" stopColor="#0D79FE"></stop>
                                        <stop offset="1" stopColor="#B39BFF"></stop>
                                    </linearGradient>
                                </defs>
                                <line x1="0" y1="10" x2="1000" y2="10" stroke="rgba(167,139,250,.85)" strokeWidth="2" strokeDasharray="7 6" vectorEffect="non-scaling-stroke"></line>
                                <polyline points="100,92 300,82 500,64 700,42 900,28" fill="none" stroke="url(#tb2DoseGradient)" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke"></polyline>
                            </svg>

                            {monthlyPrice && (
                                <span className={styles.flatPill}>${monthlyPrice} / month — flat</span>
                            )}

                            {DOSE_POINTS.map((point) => (
                                <span
                                    key={point.label}
                                    className={`${styles.dot} ${point.last ? styles.dotLast : ''} ${styles[`pos${point.left}`]}`}
                                />
                            ))}
                            {DOSE_POINTS.map((point) => (
                                <span key={`${point.label}-lbl`} className={`${styles.dlbl} ${styles[`pos${point.left}`]}`}>
                                    {point.label}
                                </span>
                            ))}
                        </div>

                        <div className={styles.axis}>
                            {AXIS.map((step) => (
                                <div key={step.month}><strong>{step.month}</strong>{step.phase}</div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className={styles.fine}>
                    {FINE_PRINT.map((block) => (
                        <div key={block.title}>
                            <h5>{block.title}</h5>
                            <p>{block.body}</p>
                        </div>
                    ))}
                </div>

            </div>
        </section>
    );
}
